'use client';

import Link from '@eliadmin/core/src/components/link';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { BlogCard } from '@eliadmin/core/src/components/blog/blog-card';
import { Carousel } from '../ui/carousel';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselProgress } from '../ui/carousel-progress';

export function Blogs({
  blogs,
  className,
}: {
  blogs: Blog[];
  className?: { section?: string; container?: string };
}) {
  const options = {
    align: 'start' as const,
  };

  const { t } = useTranslations();

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <section
      className={cn(
        'custom-section',
        'gap-6 pb-12 pr-0 pt-6 sm:gap-8 sm:pb-16 sm:pr-4 sm:pt-8 lg:gap-12 lg:pb-24 lg:pt-12',
        className?.section,
      )}
    >
      <div className={cn('custom-container', 'flex items-center justify-between pr-4 sm:pr-0')}>
        <Heading title={t('text3221')} className={{ title: 'uppercase' }} />
        <Button asChild variant={'dark'} className="hidden sm:flex">
          <Link prefetch={false} href={ROUTES.BLOG()}>
            {t('text2000')}
          </Link>
        </Button>
      </div>
      <div className={cn('custom-container', className?.container)}>
        <Carousel
          options={options}
          className={{
            main: 'w-full',
            wrapper: 'w-full',
            container: 'w-full gap-4 sm:gap-5 lg:gap-6',
            slide: 'w-full max-w-[320px] sm:max-w-[340px] lg:max-w-[384px]',
          }}
          slides={blogs.map(blog => (
            <BlogCard blog={blog} key={blog.slug} />
          ))}
        />
      </div>
      <div className="flex w-full pr-4 sm:hidden">
        <CarouselProgress selectedIndex={selectedIndex} totalSlides={blogs.length} />
      </div>
    </section>
  );
}
